import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  isValidMonthlyAddOnQuantity,
  type MonthlyAddOnKind,
  type MonthlyAddOnQuantities,
} from 'fym-shared/src/music24/monthlyAddOns';

/**
 * The query parameters of Settings -> Subscription that preset the add-on sliders (design
 * limit-upsell a1 "Choose another amount"): the quantity of the add-on after the change, for
 * example `?tab=subscription&addOnPosts=40`. Back returns to the flow that opened the page.
 */
export const ADD_ON_PRESET_PARAMS: Readonly<Record<MonthlyAddOnKind, string>> = {
  posts: 'addOnPosts',
  videoCredits: 'addOnCredits',
};

/** The preset of the URL, or null when it names no valid slider value. Pure. */
export function addOnPresetOf<T extends MonthlyAddOnQuantities>(params: URLSearchParams, saved: T): T | null {
  let preset: T | null = null;
  (Object.keys(ADD_ON_PRESET_PARAMS) as MonthlyAddOnKind[]).forEach((kind) => {
    const raw = params.get(ADD_ON_PRESET_PARAMS[kind]);
    const value = raw === null ? Number.NaN : Number(raw);
    if (raw !== null && isValidMonthlyAddOnQuantity(kind, value)) {
      preset = { ...(preset ?? saved), [kind]: value };
    }
  });
  return preset;
}

/**
 * Applies the preset of the URL to the add-on draft one time, after the saved values arrived, and
 * scrolls the add-ons panel into view. The owner still checks the total and clicks Save.
 */
export function useAddOnPreset<T extends MonthlyAddOnQuantities>(saved: T, setDraft: (draft: T) => void): void {
  const [params] = useSearchParams();
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current) {
      return;
    }
    applied.current = true;
    const preset = addOnPresetOf(params, saved);
    if (preset === null) {
      return;
    }
    setDraft(preset);
    const panel = document.querySelector<HTMLElement>('[data-testid="addons-section"]');
    if (panel !== null && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: panel.getBoundingClientRect().top + window.scrollY - 24, behavior: 'smooth' });
    }
  }, [params, saved, setDraft]);
}
